import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import { customerPaystackChargeAmount, customerPlatformBookingFeeAmount } from "@/lib/platform-settlements";

function money(currency: string, amount: number) {
  return `${currency} ${amount.toFixed(2)}`;
}

export default function InvoicePage() {
  const { businessSlug, code } = useParams();
  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState<any>(null);
  const [payerEmail, setPayerEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const [onlinePaymentsAvailable, setOnlinePaymentsAvailable] = useState(true);

  useEffect(() => {
    const run = async () => {
      if (!businessSlug || !code) return;
      setLoading(true);
      const [{ data }, { data: paymentsOk }] = await Promise.all([
        supabase.rpc("get_public_invoice", {
          _business_slug: businessSlug,
          _public_code: code,
        }),
        supabase.rpc("business_allows_customer_payments_by_slug", {
          _business_slug: businessSlug,
        }),
      ]);
      const row = (data && data[0]) || null;
      setInv(row);
      setPayerEmail(String(row?.bill_to_email || ""));
      setOnlinePaymentsAvailable(paymentsOk !== false);
      setLoading(false);
    };
    run();
  }, [businessSlug, code]);

  const items = useMemo(() => (inv?.items as any[]) || [], [inv]);
  const adjustments = useMemo(() => (inv?.adjustments as any[]) || [], [inv]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">Invoice not found or unavailable.</p>
      </div>
    );
  }

  const canPay = String(inv.status || "") !== "paid" && onlinePaymentsAvailable;
  const totalAmount = Number(inv.total_amount || 0);
  const payTotal = customerPaystackChargeAmount(totalAmount);
  const platformFee = customerPlatformBookingFeeAmount();

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-end gap-3 mb-4 print:hidden">
          <Button onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </Button>
        </div>

        {String(inv.status || "") !== "paid" && !onlinePaymentsAvailable ? (
          <div className="mb-4 print:hidden rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Online payment is temporarily unavailable for this business. Please contact them to pay this invoice.
          </div>
        ) : null}

        {canPay ? (
          <div className="mb-4 print:hidden rounded-2xl border border-border bg-accent/30 p-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Pay this invoice</p>
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to Paystack Checkout. After payment, you'll return to this invoice.
                </p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Invoice amount</span>
                  <span className="text-sm text-foreground">{money(String(inv.currency || "GHS"), totalAmount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Online payment fee</span>
                  <span className="text-sm text-foreground">{money(String(inv.currency || "GHS"), platformFee)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-sm font-semibold text-foreground">Total to pay</span>
                  <span className="text-sm font-semibold text-foreground">
                    {money(String(inv.currency || "GHS"), payTotal)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  value={payerEmail}
                  onChange={(e) => setPayerEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                />
              </div>
              <div className="flex items-end">
                <Button
                  className="w-full"
                  disabled={paying || !payerEmail.trim()}
                  onClick={async () => {
                    if (!payerEmail.trim()) return;
                    setPaying(true);
                    try {
                      const redirectPath = `/invoice/${businessSlug}/${code}`;
                      const callbackUrl = buildPaystackCallbackUrl(redirectPath);
                      const { data, error } = await supabase.functions.invoke("paystack-init", {
                        body: {
                          context: { type: "invoice_payment", invoiceId: String(inv.invoice_id) },
                          payerEmail: payerEmail.trim(),
                          callbackUrl,
                        },
                      });
                      if (error) throw error;
                      const url = String((data as any)?.authorizationUrl || "");
                      if (!url) throw new Error("Missing Paystack authorizationUrl");
                      
                      // Safari mobile fix: Use assign() with a small delay to prevent blocking
                      setTimeout(() => {
                        window.location.assign(url);
                      }, 50);
                    } catch (e: any) {
                      alert(e?.message || "Failed to start payment.");
                    } finally {
                      setPaying(false);
                    }
                  }}
                >
                  {paying ? "Redirecting…" : "Pay now"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-2xl border border-border bg-card p-6 print:border-0 print:shadow-none">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{inv.business_name}</p>
              <p className="text-sm text-muted-foreground">{inv.business_phone || ""}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Invoice</p>
              <p className="font-mono text-lg font-semibold text-foreground">{inv.invoice_number}</p>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Bill to</p>
              <p className="font-semibold text-foreground mt-1">{inv.bill_to_name}</p>
              {inv.bill_to_email ? <p className="text-sm text-muted-foreground">{inv.bill_to_email}</p> : null}
              {inv.bill_to_phone ? <p className="text-sm text-muted-foreground">{inv.bill_to_phone}</p> : null}
            </div>
            <div className="text-sm">
              <div className="text-muted-foreground">Issue date</div>
              <div className="text-foreground">{inv.issue_date}</div>
              {inv.due_date ? (
                <>
                  <div className="mt-2 text-muted-foreground">Due date</div>
                  <div className="text-foreground">{inv.due_date}</div>
                </>
              ) : null}
              <div className="mt-2">
                <span className="text-muted-foreground">Status: </span>
                <span className="text-foreground font-medium">{inv.status}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Item</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Qty</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Rate</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it: any, idx: number) => (
                  <tr key={idx} className="border-t border-border">
                    <td className="px-4 py-3">{it.description}</td>
                    <td className="px-4 py-3 text-right">{Number(it.quantity || 0)}</td>
                    <td className="px-4 py-3 text-right">{money(inv.currency, Number(it.unit_price || 0))}</td>
                    <td className="px-4 py-3 text-right">{money(inv.currency, Number(it.line_total || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-start justify-between gap-6">
            <div className="flex-1">
              {inv.notes ? (
                <>
                  <p className="text-xs uppercase text-muted-foreground">Notes</p>
                  <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{inv.notes}</p>
                </>
              ) : null}
              {inv.terms ? (
                <>
                  <p className="text-xs uppercase text-muted-foreground mt-4">Terms</p>
                  <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{inv.terms}</p>
                </>
              ) : null}
            </div>
            <div className="w-56 rounded-lg border border-border bg-muted/20 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground font-semibold">{money(inv.currency, Number(inv.subtotal_amount || 0))}</span>
              </div>
              {Number(inv.taxes_amount || 0) !== 0 ? (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Taxes</span>
                  <span className="text-foreground font-semibold">{money(inv.currency, Number(inv.taxes_amount || 0))}</span>
                </div>
              ) : null}
              {Number(inv.discounts_amount || 0) !== 0 ? (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Discounts</span>
                  <span className="text-foreground font-semibold">-{money(inv.currency, Number(inv.discounts_amount || 0))}</span>
                </div>
              ) : null}
              {adjustments.length ? (
                <div className="mt-3 pt-3 border-t border-border space-y-1 text-xs text-muted-foreground">
                  {adjustments.map((a: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between gap-2">
                      <span className="truncate">{a.label}</span>
                      <span className="whitespace-nowrap">
                        {a.type === "discount" ? "-" : ""}
                        {money(inv.currency, Number(a.computed_amount ?? a.amount ?? 0))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Total</span>
                <span className="text-sm font-semibold text-foreground">{money(inv.currency, Number(inv.total_amount || 0))}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 text-xs text-muted-foreground">
            Generated {format(new Date(), "MMM d, yyyy h:mm a")}
          </div>
        </div>
      </div>
    </div>
  );
}


